import type { FlatSearchState, SearchState } from './types';
import { getSpecificDirection } from './getSpecificDirection';

const ARRAY_SEPARATOR = ',';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getSetSearchParams(
    state: Partial<SearchState>,
): Partial<Record<FlatSearchState, string>> {
    const isDirectionSet = doesPropExist(state, 'direction');
    const isKeywordSet = doesPropExist(state, 'keyword');
    const isIncludeContainedTransactionsSet = doesPropExist(state, 'includeContainedTransactions');
    const isReplaceBulkTransactionsSet = doesPropExist(state, 'replaceBulkTransactions');
    const isStartDateSet = doesPropExist(state.dates || {}, 'startDate');
    const isEndDateSet = doesPropExist(state.dates || {}, 'endDate');
    const isMinAmountSet = doesPropExist(state.amount || {}, 'minAmount');
    const isMaxAmountSet = doesPropExist(state.amount || {}, 'maxAmount');
    const isBillIdRangeFromSet = doesPropExist(state.billIdRange || {}, 'billIdFrom');
    const isBillIdRangeToSet = doesPropExist(state.billIdRange || {}, 'billIdTo');
    const isCardIdSet = doesPropExist(state, 'cardIds');
    const isMaskedCardNumberSet = doesPropExist(state, 'maskedCardNumber');
    const isAccountsSet = doesPropExist(state, 'accounts');
    const isVariableSymbolSet = doesPropExist(state, 'variableSymbol');

    const {
        keyword,
        includeContainedTransactions,
        replaceBulkTransactions,
        dates = {},
        amount = {},
        billIdRange = {},
        variableSymbol,
    } = state;
    const { startDate, endDate } = dates;
    const { minAmount, maxAmount } = amount;
    const { billIdFrom, billIdTo } = billIdRange;
    const direction = getSpecificDirection(state.direction);
    const cardIds = state.cardIds?.join(ARRAY_SEPARATOR);
    const maskedCardNumber = state.maskedCardNumber?.join(ARRAY_SEPARATOR);
    const accounts = state.accounts?.join(ARRAY_SEPARATOR);

    return {
        ...(isDirectionSet && { direction }),
        ...(isKeywordSet && { keyword }),
        ...(isStartDateSet && { startDate }),
        ...(isEndDateSet && { endDate }),
        ...(isMinAmountSet && { minAmount }),
        ...(isMaxAmountSet && { maxAmount }),
        ...(isBillIdRangeFromSet && { billIdFrom }),
        ...(isBillIdRangeToSet && { billIdTo }),
        ...(isIncludeContainedTransactionsSet && { includeContainedTransactions }),
        ...(isReplaceBulkTransactionsSet && { replaceBulkTransactions }),
        ...(isCardIdSet && { cardIds }),
        ...(isAccountsSet && { accounts }),
        ...(isMaskedCardNumberSet && { maskedCardNumber }),
        ...(isVariableSymbolSet && { variableSymbol }),
    };
}

export function getSearchParams(state: Partial<SearchState>): Record<FlatSearchState, string> {
    return filterDefinedParams(getSetSearchParams(state));
}

export function filterDefinedParams(
    params: Record<string, string | undefined>,
): Record<keyof typeof params, string> {
    return Object.entries(params).reduce(
        (acc, [key, value]) => {
            return value ? { ...acc, [key]: String(value) } : acc;
        },
        {} as Record<keyof typeof params, string>,
    );
}

export function removeProps<T extends Record<string, unknown>>(
    obj: T,
    props: Array<keyof T>,
): Partial<T> {
    return Object.keys(obj).reduce(
        (acc, key) => (props.includes(key) ? acc : { ...acc, [key]: obj[key] }),
        {},
    );
}

function doesPropExist<Props = Record<string, any>>(props: Props, prop: keyof Props): boolean {
    // @ts-expect-error - type object
    return prop in props;
}
